// =====================================================================
// TransferPitch — Transfer Windows page (compliance dashboard).
// This file is now just data + composition; visuals/helpers live in
// src/components/transfer-windows/.
// =====================================================================
import React, { useEffect, useMemo, useState, useRef } from "react";
import { transferWindowService } from "../services/api";
import { useTranslation } from "../contexts/TranslationContext";

import {
  CONF_TO_CONTINENT,
  CONTINENTS,
  CATEGORY_LABEL,
  MOBILE_MQ,
  PAGE_SIZE,
  aggregateByCountry,
  daysBetween,
  expectedFromClosed,
  fmtDate,
} from "../components/transfer-windows/utils";
import {
  CheckIcon,
  ClockIcon,
  AlertIcon,
  CalendarIcon,
  SearchIcon,
  ChevronIcon,
  EyeIcon,
  FilterIcon,
} from "../components/transfer-windows/icons";
import Flag from "../components/transfer-windows/Flag";
import { CategoryBadge, StatusPill } from "../components/transfer-windows/Pills";
import KpiCard from "../components/transfer-windows/KpiCard";
import DetailPanel from "../components/transfer-windows/DetailPanel";

const TransferWindows = () => {
  const { currentLanguage, translatePage } = useTranslation();
  const apiLanguage = (currentLanguage || "en").toLowerCase().split("-")[0];

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [filters, setFilters] = useState({
    continent: "all",
    window: "all",
    category: "mens_professional",
    country: "all",
  });
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [page, setPage] = useState(1);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [countryFilterOpen, setCountryFilterOpen] = useState(false);
  const abortRef = useRef(null);
  const pageRef = useRef(null);
  const categoryRef = useRef(null);
  const countryFilterRef = useRef(null);

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(MOBILE_MQ).matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function")
      return undefined;
    const mql = window.matchMedia(MOBILE_MQ);
    const handler = (e) => setIsMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", handler);
      else mql.removeListener(handler);
    };
  }, []);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch all rows for the selected category. include_past so the
  // "next-expected" fallback row stays computable even when every
  // current-season window has closed.
  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    (async () => {
      try {
        const res = await transferWindowService.getAllTransferWindows(
          null,
          filters.category,
          1,
          400,
          null,
          { includePast: true, signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;
        if (res.success && res.data) {
          setRows(res.data.results || []);
        } else {
          setRows([]);
        }
        if (!ctrl.signal.aborted) setLastFetchedAt(new Date());
      } catch (e) {
        if (e?.name !== "CanceledError") {
          // eslint-disable-next-line no-console
          console.error("[TransferWindows] fetch failed", e);
        }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [filters.category]);

  // Nudge the global translation engine when async data lands.
  useEffect(() => {
    if (!translatePage) return undefined;
    if ((apiLanguage || "en") === "en") return undefined;
    if (loading) return undefined;
    const t = setTimeout(() => {
      const el = pageRef.current;
      if (el) translatePage(apiLanguage, false, el, { force: true });
    }, 150);
    return () => clearTimeout(t);
  }, [rows, loading, apiLanguage, translatePage]);

  // Outside-click dismissal for the two header dropdowns.
  useEffect(() => {
    if (!categoryOpen && !countryFilterOpen) return undefined;
    const handler = (e) => {
      if (categoryOpen && categoryRef.current && !categoryRef.current.contains(e.target))
        setCategoryOpen(false);
      if (countryFilterOpen && countryFilterRef.current && !countryFilterRef.current.contains(e.target))
        setCountryFilterOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [categoryOpen, countryFilterOpen]);

  const aggregates = useMemo(
    () => aggregateByCountry(rows, now, filters.window),
    [rows, now, filters.window],
  );

  const continentCounts = useMemo(() => {
    const base = aggregates.filter((a) => {
      if (filters.window === "summer")
        return a.window_1_label === "summer" || a.window_2_label === "summer";
      if (filters.window === "winter")
        return a.window_1_label === "winter" || a.window_2_label === "winter";
      return true;
    });
    const counts = { all: base.length };
    for (const c of CONTINENTS) {
      if (c.id === "all") continue;
      counts[c.id] = base.filter(
        (r) => CONF_TO_CONTINENT[r.confederation] === c.id,
      ).length;
    }
    return counts;
  }, [aggregates, filters.window]);

  const countryList = useMemo(
    () => [...new Set(aggregates.map((a) => a.country))].sort(),
    [aggregates],
  );

  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return aggregates
      .filter((a) => {
        if (filters.continent !== "all" && CONF_TO_CONTINENT[a.confederation] !== filters.continent)
          return false;
        if (filters.country !== "all" && a.country !== filters.country) return false;
        if ((filters.window === "summer" || filters.window === "winter") && !a.primary)
          return false;
        if (q && !(a.country || "").toLowerCase().includes(q)) return false;
        return true;
      })
      .sort((a, b) => {
        const rank = (p) =>
          p?.state === "open"
            ? 0 + p.days * 0.0001
            : p?.state === "opens"
              ? 100 + p.days * 0.0001
              : 100000;
        return rank(a.primary) - rank(b.primary);
      });
  }, [aggregates, filters, search]);

  useEffect(() => {
    setPage(1);
  }, [filters.continent, filters.window, filters.category, filters.country, search]);

  const pageCount = Math.max(1, Math.ceil(displayed.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, displayed.length);
  const visibleRows = useMemo(
    () => displayed.slice(pageStart, pageEnd),
    [displayed, pageStart, pageEnd],
  );

  const stats = useMemo(() => {
    let open = 0,
      openingWeek = 0,
      openingMonth = 0,
      closingSoon = 0;
    let nextRow = null,
      nextDays = Infinity;
    for (const a of aggregates) {
      const p = a.primary;
      if (!p) continue;
      if (p.state === "open") open++;
      if (p.state === "open" && p.days <= 7) closingSoon++;
      if (p.state === "opens" && p.days <= 7) openingWeek++;
      if (p.state === "opens" && p.days <= 30) openingMonth++;
      if (p.state === "opens" && p.days < nextDays) {
        nextDays = p.days;
        nextRow = a;
      }
    }
    return { open, openingWeek, openingMonth, closingSoon, nextRow, nextDays };
  }, [aggregates]);

  const fetchedLabel = lastFetchedAt
    ? lastFetchedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  const pageNumbers = useMemo(() => {
    if (pageCount <= 7)
      return Array.from({ length: pageCount }, (_, i) => i + 1);
    const out = [1];
    const start = Math.max(2, safePage - 1);
    const end = Math.min(pageCount - 1, safePage + 1);
    if (start > 2) out.push("…");
    for (let i = start; i <= end; i++) out.push(i);
    if (end < pageCount - 1) out.push("…");
    out.push(pageCount);
    return out;
  }, [pageCount, safePage]);

  return (
    <div
      className="bg-[#F8F9FB] min-h-screen font-[Inter,'Helvetica_Neue',sans-serif] text-[#0F172B]"
      ref={pageRef}
    >
      {/* ── Header strip ─────────────────────────────────────────── */}
      <div className="bg-white border-b border-[#E5E6E8]">
        <div className="max-w-[1440px] mx-auto px-4 md:px-14 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="m-0 font-bold text-xl text-[#202630] leading-[30px]">
              Transfer Windows
            </h1>
            <p className="mt-1 mb-0 text-sm text-[#434654] max-w-[720px] leading-[22px]">
              Track open, upcoming, and closed registration periods across FIFA member associations.
            </p>
          </div>
          {/* Stats pill: wrap its segments on mobile (was whitespace-nowrap +
              rounded-full → overflowed off-screen). Full width on mobile,
              auto on desktop. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 bg-white border border-[#E5E5E5] rounded-2xl md:rounded-full w-full md:w-auto md:flex-shrink-0">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#191B23]">
              <span className="w-2 h-2 rounded-full bg-[#22C55E]" />
              {stats.open} {stats.open === 1 ? "window" : "windows"} open now
            </span>
            <span className="text-xs font-medium text-[#C3C6D6]">|</span>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#191B23]">
              <span className="w-2 h-2 rounded-full bg-[#F59E0B]" />
              {stats.openingMonth} opening this month
            </span>
            {fetchedLabel && (
              <>
                <span className="text-xs font-medium text-[#C3C6D6]">|</span>
                <span className="text-xs font-medium text-[#434654]">
                  Updated at {fetchedLabel}
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Sub-toolbar: window tabs + search + category ─────────── */}
      <div className="bg-white border-b border-[#E5E6E8]">
        <div className="max-w-[1440px] mx-auto px-4 md:px-14 py-3.5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-2">
            {["all", "summer", "winter"].map((id) => (
              <button
                key={id}
                onClick={() => setFilters((f) => ({ ...f, window: id }))}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition ${
                  filters.window === id
                    ? "bg-[#F3F4F6] text-[#0B0B0B]"
                    : "bg-transparent text-[rgba(30,30,30,0.7)] hover:text-[#0B0B0B]"
                }`}
              >
                {id === "all" ? "All" : id.charAt(0).toUpperCase() + id.slice(1)}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="inline-flex items-center gap-2 bg-white border border-[#E6E6E6] rounded-md px-3 py-2 w-full md:w-60">
              <SearchIcon style={{ color: "#2C2C2C" }} />
              <input
                type="text"
                className="border-none outline-none bg-transparent text-sm text-[#1E1E1E] placeholder-gray-400 w-full"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="relative" ref={categoryRef}>
              <button
                type="button"
                onClick={() => setCategoryOpen((v) => !v)}
                className="inline-flex items-center gap-2 bg-[#E9F3FF] hover:bg-[#DDEBFD] px-3.5 py-2 rounded-md text-sm font-medium text-[#2283FF] transition"
              >
                <span>{CATEGORY_LABEL[filters.category]}</span>
                <ChevronIcon style={{ color: "#2283FF" }} />
              </button>
              {categoryOpen && (
                <div className="absolute top-full mt-1.5 right-0 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-30 min-w-[160px] p-1">
                  {[
                    { v: "mens_professional", l: "Men's Pro" },
                    { v: "womens_professional", l: "Women's Pro" },
                    { v: "amateur", l: "Amateur" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => {
                        setFilters((f) => ({ ...f, category: o.v }));
                        setCategoryOpen(false);
                      }}
                      className={`block w-full text-left px-3 py-2 text-sm rounded-md ${
                        filters.category === o.v
                          ? "bg-[#E9F3FF] text-[#2283FF]"
                          : "text-[#0F172B] hover:bg-[#F1F5F9]"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────── */}
      <div className="max-w-[1440px] mx-auto px-4 md:px-14 pt-6 pb-12 flex flex-col gap-5">
        {/* Continent pills + All Countries filter */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 ">
          <div className="flex items-center gap-2 flex-wrap border border-[#E5E6E8] rounded-lg p-0.5">
            {CONTINENTS.map((c) => (
              <button
                key={c.id}
                onClick={() => setFilters((f) => ({ ...f, continent: c.id }))}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap border ${
                  filters.continent === c.id
                    ? " text-[#0F172B] bg-white"
                    : "bg-transparent border-transparent text-[#45556C] hover:bg-white hover:text-[#0F172B]"
                }`}
              >
                {c.label} ({continentCounts[c.id] ?? 0})
              </button>
            ))}
          </div>
          <div className="relative" ref={countryFilterRef}>
            <button
              type="button"
              onClick={() => setCountryFilterOpen((v) => !v)}
              className={`inline-flex items-center gap-2 bg-white border rounded-lg px-3.5 py-2.5 text-sm font-medium whitespace-nowrap hover:bg-[#F8FAFC] transition ${
                filters.country !== "all"
                  ? "border-blue-200 text-[#155DFC]"
                  : "border-[#E2E8F0] text-[#45556C]"
              }`}
            >
              <FilterIcon />
              <span>{filters.country === "all" ? "All Countries" : filters.country}</span>
              <ChevronIcon />
            </button>
            {countryFilterOpen && (
              <div className="absolute top-full mt-1.5 right-0 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-30 w-[260px] max-h-[360px] p-1 flex flex-col">
                <button
                  onClick={() => {
                    setFilters((f) => ({ ...f, country: "all" }));
                    setCountryFilterOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2 text-sm rounded-md ${
                    filters.country === "all"
                      ? "bg-[#E9F3FF] text-[#2283FF]"
                      : "text-[#0F172B] hover:bg-[#F1F5F9]"
                  }`}
                >
                  All Countries
                </button>
                <div className="overflow-y-auto flex-1">
                  {countryList.map((c) => (
                    <button
                      key={c}
                      onClick={() => {
                        setFilters((f) => ({ ...f, country: c }));
                        setCountryFilterOpen(false);
                      }}
                      className={`block w-full text-left px-3 py-2 text-sm rounded-md ${
                        filters.country === c
                          ? "bg-[#E9F3FF] text-[#2283FF]"
                          : "text-[#0F172B] hover:bg-[#F1F5F9]"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KpiCard
            label="OPEN NOW"
            value={stats.open}
            sub={
              stats.open === 0
                ? "No active windows today"
                : `${stats.open} active window${stats.open === 1 ? "" : "s"}`
            }
            icon={CheckIcon}
          />
          <KpiCard
            label="OPENING SOON"
            value={stats.openingWeek}
            sub={
              stats.openingWeek === 1
                ? "1 opening this week"
                : `${stats.openingWeek} opening this week`
            }
            icon={ClockIcon}
          />
          <KpiCard
            label="CLOSING SOON"
            value={stats.closingSoon}
            sub={
              stats.closingSoon === 0
                ? "None in the next 7 days"
                : `${stats.closingSoon} closing this week`
            }
            icon={AlertIcon}
          />
          <KpiCard
            label="NEXT MAJOR WINDOW"
            icon={CalendarIcon}
            custom={
              stats.nextRow ? (
                <div className="inline-flex items-center gap-3">
                  <Flag
                    code={stats.nextRow.country_code}
                    country={stats.nextRow.country}
                    size={26}
                  />
                  <span className="text-xl font-semibold text-[#191B23] leading-7">
                    {stats.nextRow.country}
                  </span>
                </div>
              ) : (
                <div className="text-base font-medium text-slate-400">
                  None scheduled
                </div>
              )
            }
            sub={
              stats.nextRow
                ? `Opens in ${stats.nextDays} day${stats.nextDays === 1 ? "" : "s"} · ${fmtDate(stats.nextRow.primary?.start)}`
                : "No upcoming window in the current view"
            }
          />
        </div>

        {/* Table card */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
          {!isMobile ? (
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">Country</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">Category</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">Open Date</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">Open Until</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">Duration</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">Status</th>
                  <th className="text-center text-xs font-semibold uppercase tracking-wider text-[#45556C] px-6 py-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="p-8 text-center text-[#62748E] text-sm">
                      Loading transfer windows…
                    </td>
                  </tr>
                )}
                {!loading &&
                  visibleRows.map((row) => {
                    const isOpen = expanded === row.id;
                    const p = row.primary;
                    const span = p ? daysBetween(p.start, p.end) + 1 : 0;
                    const fallback = !p || p.state === "closed" ? expectedFromClosed(row, now) : null;
                    return (
                      <React.Fragment key={row.id}>
                        <tr
                          onClick={() => setExpanded((id) => (id === row.id ? null : row.id))}
                          className={`cursor-pointer transition border-b border-[#F1F5F9] ${
                            isOpen ? "bg-[#F8FAFC]/50" : "hover:bg-[#F8FAFC]"
                          }`}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <Flag code={row.country_code} country={row.country} size={28} />
                              <div className="flex flex-col leading-tight">
                                <span
                                  className="font-medium text-base text-[#0F172B]"
                                  style={{ letterSpacing: "-0.3px" }}
                                >
                                  {row.country}
                                </span>
                                {row.confederation && (
                                  <span className="font-medium text-xs text-gray-500 mt-0.5">
                                    {row.confederation}
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <CategoryBadge category={row.category} />
                          </td>
                          <td className="px-6 py-4 text-sm text-[#0F172B]" style={{ letterSpacing: "-0.15px" }}>
                            {fmtDate(p?.start)}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#0F172B]" style={{ letterSpacing: "-0.15px" }}>
                            {fmtDate(p?.end)}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#45556C]" style={{ letterSpacing: "-0.15px" }}>
                            {p ? `${span}d` : "—"}
                          </td>
                          <td className="px-6 py-4">
                            <StatusPill primary={p} fallbackLine={fallback} />
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpanded((id) => (id === row.id ? null : row.id));
                              }}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition ${
                                isOpen
                                  ? "text-[#155DFC] hover:bg-[#EFF6FF]"
                                  : "text-[#353535] hover:bg-[#F1F5F9]"
                              }`}
                              style={{ letterSpacing: "-0.15px" }}
                            >
                              <EyeIcon />
                              <span>View</span>
                            </button>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="border-b border-[#F1F5F9]">
                            <td colSpan={7} className="px-6 pb-5 pt-1 bg-[#F8FAFC]/50">
                              <DetailPanel row={row} now={now} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                {!loading && displayed.length === 0 && (
                  <tr>
                    <td colSpan={7} className="p-12 text-center text-[#62748E] text-sm">
                      No transfer windows match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col gap-3 p-3">
              {loading && (
                <div className="p-8 text-center text-[#62748E] text-sm">
                  Loading transfer windows…
                </div>
              )}
              {!loading &&
                visibleRows.map((row) => {
                  const isOpen = expanded === row.id;
                  const p = row.primary;
                  const span = p ? daysBetween(p.start, p.end) + 1 : 0;
                  const fallback = !p || p.state === "closed" ? expectedFromClosed(row, now) : null;
                  return (
                    <div
                      key={row.id}
                      onClick={() => setExpanded((id) => (id === row.id ? null : row.id))}
                      className={`bg-white border border-[#E2E8F0] rounded-xl p-3 cursor-pointer ${
                        isOpen ? "shadow-md" : ""
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <Flag code={row.country_code} country={row.country} size={26} />
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="font-medium text-base text-[#0F172B]">
                            {row.country}
                          </span>
                          {row.confederation && (
                            <span className="text-xs text-gray-500">
                              {row.confederation}
                            </span>
                          )}
                        </div>
                        <StatusPill primary={p} fallbackLine={fallback} />
                      </div>
                      <div className="grid grid-cols-2 gap-2.5 mt-2.5">
                        <div>
                          <div className="text-[11px] text-slate-400">Category</div>
                          <div className="text-sm text-[#0F172B] font-medium">
                            <CategoryBadge category={row.category} />
                          </div>
                        </div>
                        <div>
                          <div className="text-[11px] text-slate-400">Open</div>
                          <div className="text-sm text-[#0F172B] font-medium">{fmtDate(p?.start)}</div>
                        </div>
                        <div>
                          <div className="text-[11px] text-slate-400">Until</div>
                          <div className="text-sm text-[#0F172B] font-medium">{fmtDate(p?.end)}</div>
                        </div>
                        <div>
                          <div className="text-[11px] text-slate-400">Duration</div>
                          <div className="text-sm text-[#0F172B] font-medium">
                            {p ? `${span}d` : "—"}
                          </div>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="mt-3">
                          <DetailPanel row={row} now={now} />
                        </div>
                      )}
                    </div>
                  );
                })}
              {!loading && displayed.length === 0 && (
                <div className="p-8 text-center text-[#62748E] text-sm">
                  No transfer windows match the current filters.
                </div>
              )}
            </div>
          )}

          {/* Pagination footer — stacks on mobile (count above, controls
              below), controls wrap + center so the page numbers never
              overflow a ~375px screen. Desktop unchanged (sm:flex-row +
              sm:justify-between). */}
          {!loading && displayed.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 md:px-6 py-4 border-t border-[#E2E8F0] bg-white gap-3">
              <span
                className="text-sm text-[#45556C] text-center sm:text-left"
                style={{ letterSpacing: "-0.15px" }}
              >
                Showing {pageStart + 1}-{pageEnd} of {displayed.length} results
              </span>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="bg-white border border-[#CAD5E2] px-3.5 py-1.5 rounded-xl text-sm font-medium text-[#45556C] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                {pageNumbers.map((n, i) =>
                  n === "…" ? (
                    <span key={`e${i}`} className="px-1 text-slate-400">…</span>
                  ) : (
                    <button
                      key={n}
                      onClick={() => setPage(n)}
                      className={`w-8 h-8 flex-shrink-0 rounded-xl text-sm font-medium transition ${
                        n === safePage
                          ? "bg-[#155DFC] text-white"
                          : "text-[#45556C] hover:bg-[#F1F5F9]"
                      }`}
                    >
                      {n}
                    </button>
                  ),
                )}
                <button
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  disabled={safePage >= pageCount}
                  className="bg-white border border-[#CAD5E2] px-3.5 py-1.5 rounded-xl text-sm font-medium text-[#45556C] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransferWindows;
